import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SaveRegular } from '@fluentui/react-icons';
import { useDashboard } from '../contexts/DashboardContext';
import { invalidateChart, invalidateDashboard } from '../components/charts/hooks';
import { useTheme } from '../contexts/ThemeContext';
import { useTopBar, useTopBarBreadcrumbs, useTopBarTitleNode } from '../contexts/TopBarContext';
import InlineEditableTitle from '../components/InlineEditableTitle';
import { Chart as ApiChart, ChartData, ChartWithColumns, DatasetViewColumn } from '../api';
import { chartsService } from '../api/chartsService';
import { dataModelsService } from '../api/dataModelsService';
import { getCachedChartData, setCachedChartData } from '../components/charts/hooks';
import { DashboardFilterBar } from '../components/DashboardFilterBar';
import EditorPanel from '../components/edit/EditorPanel';
import { EditorDisplayNamesProvider } from '../components/edit/EditorDisplayNamesContext';
import ChartPreview from '../components/edit/ChartPreview';
import {
  type ChartEditorState,
  type PaletteOption,
  toPersistedConfig,
  getCapabilities,
} from '../components/edit/visualConfig';
import { useChartFilters } from './hooks/useChartFilters';
import { useEditorStateSync } from './hooks/useEditorStateSync';
import { useChartPreview } from './hooks/useChartPreview';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  leftPanel: {
    overflow: 'hidden',
    minWidth: '200px',
  },
  splitter: {
    width: '4px',
    cursor: 'col-resize',
    backgroundColor: tokens.colorNeutralStroke2,
    flexShrink: 0,
    position: 'relative' as const,
    zIndex: 1,
  },
  rightPanel: {
    overflow: 'hidden',
    minWidth: '200px',
  },
});

/**
 * Chart Edit Page
 *
 * Provides UI for editing chart configuration with AI, Visual, and Query
 * modes. Shares the same `DashboardFilterBar` and per-dashboard `filterStore`
 * as the Dashboard page, so filter state is preserved when navigating between
 * dashboard view and chart edit.
 */
const ChartEditPage: React.FC = () => {
  const { id: dashboardId, chartId } = useParams<{ id: string; chartId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recompile = searchParams.get('recompile') === 'true';
  const { currentDashboardDetails, updateChartInDetails, addChartToDetails, loadDashboardDetails, isLoadingDetails, setScrollToChartId } = useDashboard();
  const { isDarkMode } = useTheme();
  const { setTitle, setActions } = useTopBar();
  const styles = useStyles();

  const isNewChart = !chartId || chartId === 'new';

  // ── Chart state ──────────────────────────────────────────────────────────

  const defaultNewChart: ApiChart = useMemo(() => ({
    id: 'new',
    dashboardId: dashboardId ?? '',
    datasetViewId: currentDashboardDetails?.datasetViewId
      ?? currentDashboardDetails?.charts?.[0]?.datasetViewId
      ?? null,
    chartType: 'bar' as const,
    title: 'New Chart',
    description: null,
    positionConfig: null,
    chartStyle: null,
    chartFieldsConfig: null,
    sql: '',
    fieldMappings: null,
    aiMetadata: null,
    orderIndex: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    datasetView: null,
  }), [dashboardId, currentDashboardDetails?.datasetViewId, currentDashboardDetails?.charts]);

  const [chart, setChart] = useState<ApiChart | null>(() => {
    if (isNewChart) return defaultNewChart;
    if (!chartId || !currentDashboardDetails) return null;
    return currentDashboardDetails.charts?.find((c) => c.id === chartId) ?? null;
  });
  const [isLoadingChart, setIsLoadingChart] = useState(() => {
    if (isNewChart) return false;
    if (!chartId || !currentDashboardDetails) return true;
    return !currentDashboardDetails.charts?.some((c) => c.id === chartId);
  });
  const [chartData, setChartData] = useState<ChartData | null>(() => {
    if (!dashboardId || !chartId) return null;
    const cached = getCachedChartData(dashboardId, chartId, JSON.stringify({}));
    return cached?.data ?? null;
  });
  const [isLoadingChartData, setIsLoadingChartData] = useState(false);
  const [chartError, setChartError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [splitPercent, setSplitPercent] = useState(60);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Inline editable chart title ─────────────────────────────────────────
  const handleChartTitleSave = useCallback(async (newTitle: string) => {
    if (!chart) return;

    const oldTitle = chart.title;

    // Update local state immediately
    setChart((prev) => prev ? { ...prev, title: newTitle } : prev);

    // Persist to server for existing charts
    if (!isNewChart && chartId) {
      try {
        const updatedChart = await chartsService.updateChart(chartId, { title: newTitle });
        updateChartInDetails(chartId, updatedChart);
      } catch (err) {
        console.error('Failed to rename chart:', err);
        // Revert on failure
        setChart((prev) => prev ? { ...prev, title: oldTitle } : prev);
      }
    }
  }, [chart, chartId, isNewChart, updateChartInDetails]);

  // ── Dataset view columns (for field mapping editor) ──────────────────────

  const [viewColumns, setViewColumns] = useState<DatasetViewColumn[]>([]);

  // ── Custom hooks ─────────────────────────────────────────────────────────

  const dashboardTheme = (currentDashboardDetails?.settings as any)?.theme ?? 'default';
  const vcSync = useEditorStateSync(chart, isNewChart, dashboardTheme);
  const {
    editorState,
    initialEditorState,
    initialEditorStateRef,
    isVisualDirty,
    isPendingFieldUpdate,
    setIsPendingFieldUpdate,
    handleEditorStateChange,
    syncEditorStateFromChart,
    fieldsJsonRef,
    sortLimitJsonRef,
  } = vcSync;

  const preview = useChartPreview({
    chart,
    editorState,
    dashboardId,
    fallbackDatasetViewId: currentDashboardDetails?.charts?.[0]?.datasetViewId,
    syncEditorStateFromChart,
    setIsPendingFieldUpdate,
    fieldsJsonRef,
    sortLimitJsonRef,
  });
  const {
    previewChart,
    previewChartId,
    previewChartIdRef,
    refreshTrigger,
    setRefreshTrigger,
    handlePreviewUpdate,
    handleSqlExecute,
    handleSqlErrorClear,
    isSqlExecuting,
    sqlError,
    resetPreviewState,
  } = preview;

  const filters = useChartFilters(dashboardId);
  const {
    filterValues,
    resolvedFilters,
    defaultFilterValuesRef,
    handleFilterChange,
    handleFilterReset,
    handleResetAll,
    handleFilterDefault,
    handleFilterReady,
    setRefreshCallback,
  } = filters;

  // Wire up filter default → refresh trigger
  useEffect(() => {
    setRefreshCallback(() => setRefreshTrigger((v) => v + 1));
  }, [setRefreshCallback, setRefreshTrigger]);

  // ── Reset state when chartId changes (e.g. existing chart → new chart) ──

  const prevChartIdRef = useRef(chartId);
  useEffect(() => {
    if (chartId === prevChartIdRef.current) return;
    prevChartIdRef.current = chartId;

    setChartData(null);
    resetPreviewState();
    setChartError(null);
    setIsLoadingChartData(false);
    chartFetchedRef.current = false;

    if (isNewChart) {
      setChart(defaultNewChart);
      setIsLoadingChart(false);
    } else {
      const found = currentDashboardDetails?.charts?.find((c) => c.id === chartId) ?? null;
      setChart(found);
      setIsLoadingChart(!found);
    }
  }, [chartId, isNewChart, defaultNewChart, currentDashboardDetails, resetPreviewState]);

  // ── Series names (derived from chart data for color overrides UI) ──────

  const seriesNames = useMemo<string[]>(() => {
    if (!chartData) return [];

    const meta = chartData.meta;
    const caps = getCapabilities(editorState.chartType);

    // Single-series charts (e.g. KPI): derive name from the measure field label,
    // falling back to the first data column name (e.g. "Avg Probability").
    if (caps.singleSeriesLabel) {
      const valueField = editorState.fields?.value;
      const fieldLabel = valueField?.label;
      const dataLabel = chartData.columns[0]?.name;
      return [fieldLabel ?? dataLabel ?? 'Value'];
    }

    if (meta?.series && chartData.columns.some((c) => c.name === meta.series)) {
      const sIdx = chartData.columns.findIndex((c) => c.name === meta.series);
      if (sIdx >= 0) {
        const unique = new Set<string>();
        for (const row of chartData.rows) {
          unique.add(String(row[sIdx] ?? ''));
        }
        return Array.from(unique);
      }
    }

    if (!caps.axis && meta?.x) {
      const xIdx = chartData.columns.findIndex((c) => c.name === meta.x);
      if (xIdx >= 0) {
        const unique = new Set<string>();
        for (const row of chartData.rows) {
          unique.add(String(row[xIdx] ?? ''));
        }
        return Array.from(unique);
      }
    }

    if (meta?.y && meta.y.length > 0) return meta.y;
    if (chartData.columns.length > 1) {
      return chartData.columns.slice(1).map((c) => c.name);
    }
    return [];
  }, [chartData, editorState.chartType, editorState.fields]);

  // Extract unique X-axis category names (for per-category color overrides)
  const categoryNames = useMemo<string[]>(() => {
    if (!chartData?.meta?.x) return [];
    const xIdx = chartData.columns.findIndex((c) => c.name === chartData.meta!.x);
    if (xIdx < 0) return [];
    const unique = new Set<string>();
    for (const row of chartData.rows) {
      unique.add(String(row[xIdx] ?? ''));
    }
    return Array.from(unique);
  }, [chartData]);

  // ── Refresh data handler ──────────────────────────────────────────────

  const handleRefreshData = useCallback(() => {
    if (!dashboardId) return;
    invalidateDashboard(dashboardId);
    setChartData(null);
    setRefreshTrigger((v) => v + 1);
  }, [dashboardId, setRefreshTrigger]);

  // ── Load dashboard details if not already loaded ─────────────────────────

  useEffect(() => {
    if (dashboardId && (!currentDashboardDetails || currentDashboardDetails.id !== dashboardId)) {
      loadDashboardDetails(dashboardId);
    }
  }, [dashboardId, currentDashboardDetails, loadDashboardDetails]);

  // ── Flush visual config to Redis on tab switch (Visual → other) ──────────

  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;

  const handleTabSwitch = useCallback(
    (fromTab: string, _toTab: string) => {
      if (fromTab !== 'visual') return;

      const currentJson = JSON.stringify(editorStateRef.current);
      if (currentJson === initialEditorStateRef.current) return;

      const currentPreviewKey = previewChartIdRef.current;
      if (!currentPreviewKey) return;

      const dvId = chart?.datasetViewId ?? currentDashboardDetails?.charts?.[0]?.datasetViewId;
      if (!dashboardId || !dvId) return;

      const { chartStyle, chartFieldsConfig } = toPersistedConfig(
        editorStateRef.current,
        chart?.chartStyle as Record<string, unknown> | null,
        chart?.chartFieldsConfig as Record<string, unknown> | null,
      );

      chartsService.updateChartPreview({
        chartId: chart?.id ?? 'new',
        datasetViewId: dvId,
        previewKey: currentPreviewKey,
        chartStyle: chartStyle as Record<string, unknown>,
        chartFieldsConfig: chartFieldsConfig as Record<string, unknown>,
        chartType: editorStateRef.current.chartType,
      }).catch((err) => {
        console.error('Failed to flush visual config to preview:', err);
      });
    },
    [dashboardId, chart?.id, chart?.datasetViewId, chart?.chartStyle, chart?.chartFieldsConfig, currentDashboardDetails?.charts, initialEditorStateRef, previewChartIdRef],
  );

  // ── Fetch original chart metadata + view columns (skip for new charts) ──

  const chartFetchedRef = useRef(false);
  useEffect(() => {
    if (isNewChart || chartFetchedRef.current) return;
    if (!chartId) return;

    chartFetchedRef.current = true;

    const fetchChart = async () => {
      setIsLoadingChart(true);
      setChartError(null);
      try {
        const fetched = await chartsService.getChart(chartId);
        setChart(fetched);
        syncEditorStateFromChart(fetched);
        const enriched = fetched as ChartWithColumns;
        if (enriched.datasetViewColumns && Array.isArray(enriched.datasetViewColumns)) {
          setViewColumns(enriched.datasetViewColumns);
        }
      } catch (err) {
        console.error('Failed to load chart:', err);
        setChartError('Failed to load chart');
        setChart(null);
      } finally {
        setIsLoadingChart(false);
      }
    };
    fetchChart();
  }, [chartId, dashboardId, isNewChart, syncEditorStateFromChart]);

  // ── Fetch view columns for new charts ────────────────────────────────────

  const viewColumnsFetchedRef = useRef(false);
  useEffect(() => {
    if (!isNewChart) return;
    if (viewColumnsFetchedRef.current) return;
    if (viewColumns.length > 0) return;

    const dvId = currentDashboardDetails?.datasetViewId;
    if (!dvId) return;

    viewColumnsFetchedRef.current = true;

    dataModelsService.getViewColumns(dvId)
      .then((cols) => {
        setViewColumns(cols);
      })
      .catch((err) => {
        console.error('Failed to load dataset view columns for new chart:', err);
      });
  }, [isNewChart, currentDashboardDetails?.datasetViewId, viewColumns.length]);

  // ── Load chart data ──────────────────────────────────────────────────────

  useEffect(() => {
    const loadChartData = async () => {
      if (!dashboardId) return;
      const chartIdToLoad = previewChartId || chart?.id;
      if (!chartIdToLoad || chartIdToLoad === 'new') return;

      if (isNewChart && !previewChartId) return;

      // If dashboard has filters with defaults (e.g. date range) but
      // resolvedFilters is still empty, wait for handleFilterDefault to fire.
      // This prevents an initial fetch with empty filters that misses cache.
      const hasFilters = (currentDashboardDetails?.filters?.length ?? 0) > 0;
      const filtersEmpty = Object.keys(resolvedFilters).length === 0;
      if (hasFilters && filtersEmpty && refreshTrigger === 0) return;

      const filtersKey = JSON.stringify(resolvedFilters);

      // Check cache first — works for both initial load and default filter resolution
      const cached = getCachedChartData(dashboardId, chartIdToLoad, filtersKey);
      if (cached && refreshTrigger === 0) {
        setChartData(cached.data);
        return;
      }

      setIsLoadingChartData(true);
      setChartError(null);
      try {
        const forceRefresh = refreshTrigger > 0;
        const response = await chartsService.getChartData(chartIdToLoad, {
          filters: resolvedFilters,
          refresh: forceRefresh,
          recompile,
        });
        setChartData(response.data);
        setCachedChartData(dashboardId, chartIdToLoad, filtersKey, response.data);
        setIsPendingFieldUpdate(false);
      } catch (err) {
        console.error('Failed to load chart data:', err);
        setChartError('Failed to load chart data');
        setIsPendingFieldUpdate(false);
      } finally {
        setIsLoadingChartData(false);
      }
    };
    loadChartData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- use chart?.id to avoid re-fetch on object ref change; resolvedFilters triggers on filter changes including defaults
  }, [dashboardId, chart?.id, previewChartId, resolvedFilters, refreshTrigger, isNewChart, setIsPendingFieldUpdate]);

  // ── Build effective chart for preview ────────────────────────────────────

  const effectivePreviewChart = useMemo<ApiChart | null>(() => {
    const baseChart = previewChart || chart;
    if (!baseChart) return null;

    const { chartStyle, chartFieldsConfig } = toPersistedConfig(
      editorState,
      baseChart.chartStyle as Record<string, unknown> | null,
      baseChart.chartFieldsConfig as Record<string, unknown> | null,
    );
    return {
      ...baseChart,
      chartType: editorState.chartType as ApiChart['chartType'],
      chartStyle: chartStyle as ApiChart['chartStyle'],
      chartFieldsConfig: chartFieldsConfig as ApiChart['chartFieldsConfig'],
    };
  }, [previewChart, chart, editorState]);

  // ── Stable chart preview (freeze during field-change debounce) ──────────

  const lastStableSnapshotRef = useRef<{ chart: ApiChart; data: ChartData | null; state: ChartEditorState } | null>(null);

  useEffect(() => {
    if (isPendingFieldUpdate) return;
    const currentChart = effectivePreviewChart || chart;
    if (currentChart) {
      lastStableSnapshotRef.current = { chart: currentChart, data: chartData, state: editorState };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot captures all preview inputs; ref assignment is intentional
  }, [effectivePreviewChart, chart, chartData, editorState, isPendingFieldUpdate]);

  const stableChart = useMemo(() => {
    if (isPendingFieldUpdate && lastStableSnapshotRef.current) {
      return lastStableSnapshotRef.current;
    }
    const currentChart = effectivePreviewChart || chart;
    if (currentChart) {
      return { chart: currentChart, data: chartData, state: editorState };
    }
    return lastStableSnapshotRef.current;
  }, [effectivePreviewChart, chart, chartData, editorState, isPendingFieldUpdate]);

  // ── Navigation ───────────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    navigate(dashboardId ? `/dashboards/${dashboardId}` : '/dashboards');
  }, [dashboardId, navigate]);

  // ── Save ─────────────────────────────────────────────────────────────────

  // Build a visual config for saving — if the palette matches the dashboard
  // theme, revert it to the static default so delta-save strips it (inherit).
  const buildSaveEditorState = useCallback((): ChartEditorState => {
    if (editorState.chartStyle.palette === dashboardTheme) {
      return {
        ...editorState,
        chartStyle: { ...editorState.chartStyle, palette: 'default' as PaletteOption },
      };
    }
    return editorState;
  }, [editorState, dashboardTheme]);

  const handleSave = useCallback(async () => {
    if (!dashboardId) return;

    const effectivePreviewKey = previewChartId;
    const saveState = buildSaveEditorState();
    setIsSaving(true);
    try {
      if (isNewChart) {
        if (!effectivePreviewKey) return;
        const newChart = await chartsService.createChartFromPreview(dashboardId, effectivePreviewKey);

        // Check if user renamed the chart from the default or AI-generated title
        const userTitle = chart?.title && chart.title !== newChart.title ? chart.title : undefined;

        if (isVisualDirty || userTitle) {
          try {
            const { chartStyle, chartFieldsConfig } = toPersistedConfig(
              saveState,
              newChart.chartStyle as Record<string, unknown> | null,
              newChart.chartFieldsConfig as Record<string, unknown> | null,
            );
            const updatePayload: Record<string, unknown> = {};
            if (userTitle) updatePayload.title = userTitle;
            if (isVisualDirty) {
              updatePayload.chartType = saveState.chartType;
              updatePayload.chartStyle = chartStyle;
              updatePayload.chartFieldsConfig = chartFieldsConfig;
            }
            const updatedChart = await chartsService.updateChart(newChart.id, updatePayload as any);
            addChartToDetails(updatedChart);
          } catch (styleErr) {
            console.error('Chart created but post-create update failed:', styleErr);
            addChartToDetails(newChart);
          }
        } else {
          addChartToDetails(newChart);
        }

        setScrollToChartId(newChart.id);
      } else {
        if (!chartId) return;

        if (effectivePreviewKey) {
          const updatePayload: Record<string, any> = {
            previewKey: effectivePreviewKey,
          };
          if (isVisualDirty) {
            const { chartStyle, chartFieldsConfig } = toPersistedConfig(
              saveState,
              chart?.chartStyle as Record<string, unknown> | null,
              chart?.chartFieldsConfig as Record<string, unknown> | null,
            );
            updatePayload.chartType = saveState.chartType;
            updatePayload.chartStyle = chartStyle;
            updatePayload.chartFieldsConfig = chartFieldsConfig;
          }
          const updatedChart = await chartsService.updateChart(chartId, updatePayload as any);
          updateChartInDetails(chartId, updatedChart);
        } else if (isVisualDirty) {
          const { chartStyle, chartFieldsConfig } = toPersistedConfig(
            saveState,
            chart?.chartStyle as Record<string, unknown> | null,
            chart?.chartFieldsConfig as Record<string, unknown> | null,
          );
          const updatedChart = await chartsService.updateChart(chartId, {
            chartType: saveState.chartType as any,
            chartStyle: chartStyle as any,
            chartFieldsConfig: chartFieldsConfig as any,
          });
          updateChartInDetails(chartId, updatedChart);
        }

        invalidateChart(dashboardId, chartId);

        if (chartData) {
          setCachedChartData(dashboardId, chartId, JSON.stringify(resolvedFilters), chartData);
        }
      }
      navigate(`/dashboards/${dashboardId}`);
    } catch (err) {
      console.error('Failed to save chart:', err);
      setChartError('Failed to save chart changes');
    } finally {
      setIsSaving(false);
    }
  }, [dashboardId, chartId, previewChartId, isNewChart, isVisualDirty, editorState, chart, chartData, filterValues, defaultFilterValuesRef, navigate, updateChartInDetails, addChartToDetails, setScrollToChartId, buildSaveEditorState]);

  const isDirty = previewChart !== null || isVisualDirty;

  // ── TopBar ───────────────────────────────────────────────────────────────

  const dashboardName = currentDashboardDetails?.name || 'Dashboard';
  const chartName = previewChart?.title ?? chart?.title ?? 'Chart';
  useTopBarBreadcrumbs([
    { text: 'Dashboards', to: '/dashboards' },
    { text: dashboardName, to: dashboardId ? `/dashboards/${dashboardId}` : '/dashboards' },
    { text: chartName },
  ]);

  // Set plain-text title as fallback (screen readers / document.title)
  useEffect(() => {
    setTitle(chart ? chart.title : 'Edit Chart');
    return () => setTitle('');
  }, [chart, setTitle]);

  // Editable title node in TopBar
  const titleNode = useMemo(
    () => (
      <InlineEditableTitle
        value={chartName}
        onSave={handleChartTitleSave}
        placeholder="Untitled Chart"
      />
    ),
    [chartName, handleChartTitleSave],
  );
  useTopBarTitleNode(titleNode);

  const topBarActions = useMemo(
    () => (
      <>
        <Button appearance="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          appearance="primary"
          icon={isSaving ? <Spinner size="tiny" /> : <SaveRegular />}
          onClick={handleSave}
          disabled={!isDirty || isSaving}
          style={{ marginLeft: '8px' }}
        >
          {isSaving ? 'Saving' : 'Save'}
        </Button>
      </>
    ),
    [handleCancel, handleSave, isDirty, isSaving],
  );

  useEffect(() => {
    setActions(topBarActions);
    return () => setActions(null);
  }, [topBarActions, setActions]);

  // ── Splitter drag ────────────────────────────────────────────────────────

  const handleSplitterMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const pct = ((moveEvent.clientX - rect.left) / rect.width) * 100;
        setSplitPercent(Math.min(80, Math.max(20, pct)));
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [],
  );

  // ── Early returns ────────────────────────────────────────────────────────

  if (!isNewChart && isLoadingDetails && !currentDashboardDetails) {
    return (
      <div className={styles.page} data-theme={isDarkMode ? 'dark' : 'light'}>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Spinner size="large" label="Loading chart editor..." />
        </div>
      </div>
    );
  }

  if (!chart && !isLoadingChart && chartError) {
    return (
      <div className={styles.page} data-theme={isDarkMode ? 'dark' : 'light'}>
        <div style={{ padding: '40px' }}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Chart Not Found</MessageBarTitle>
              The chart you're trying to edit does not exist or has expired.
            </MessageBarBody>
          </MessageBar>
        </div>
      </div>
    );
  }

  if (isLoadingChart || !chart) {
    return (
      <div className={styles.page} data-theme={isDarkMode ? 'dark' : 'light'}>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Spinner size="large" label="Loading chart..." />
        </div>
      </div>
    );
  }

  const dashboardFilters = currentDashboardDetails?.filters || [];

  return (
    <div className={styles.page} data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Shared filter bar — same component as Dashboard */}
      {dashboardFilters.length > 0 && dashboardId && (
        <DashboardFilterBar
          dashboardId={dashboardId}
          filters={dashboardFilters}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          onFilterDefault={handleFilterDefault}
          onFilterReset={handleFilterReset}
          onResetAll={handleResetAll}
          onFilterReady={handleFilterReady}
          onRefreshData={handleRefreshData}
        />
      )}

      {/* Main content area */}
      <div className={styles.mainContent} ref={containerRef}>
        <div className={styles.leftPanel} style={{ width: `calc(${splitPercent}% - 2px)` }}>
          <EditorDisplayNamesProvider dataModelId={
            chart.datasetView?.dataModelId ?? currentDashboardDetails?.charts
              ?.find((item) => item.datasetViewId === chart.datasetViewId)?.datasetView?.dataModelId
          }>
          <EditorPanel
            chart={chart}
            previewChart={previewChart}
            onPreviewUpdate={handlePreviewUpdate}
            isNewChart={isNewChart}
            dashboardId={dashboardId}
            previewChartId={previewChartId}
            filters={resolvedFilters}
            onTabSwitch={handleTabSwitch}
            chartSuggestions={currentDashboardDetails?.chartSuggestions ?? null}
            editSuggestions={
              !isNewChart && chartId && currentDashboardDetails?.chartSuggestions?.editSuggestions
                ? (currentDashboardDetails.chartSuggestions.editSuggestions as Record<string, Array<{ title: string; description: string; prompt: string }>>)[chartId] ?? null
                : null
            }
            editorState={editorState}
            initialEditorState={initialEditorState}
            onEditorStateChange={handleEditorStateChange}
            seriesNames={seriesNames}
            categoryNames={categoryNames}
            viewColumns={viewColumns}
            dashboardTheme={(currentDashboardDetails?.settings as any)?.theme ?? 'default'}
            viewName={chart.datasetView?.name}
            onSqlExecute={handleSqlExecute}
            onSqlErrorClear={handleSqlErrorClear}
            isSqlExecuting={isSqlExecuting}
            sqlError={sqlError}
          />
          </EditorDisplayNamesProvider>
        </div>

        <div className={styles.splitter} onMouseDown={handleSplitterMouseDown} />

        <div className={styles.rightPanel} style={{ width: `calc(${100 - splitPercent}% - 2px)` }}>
          <ChartPreview
            chart={stableChart?.chart ?? effectivePreviewChart ?? chart}
            chartData={(stableChart?.data ?? chartData) ?? undefined}
            isLoading={isLoadingChartData}
            error={chartError}
            editorState={stableChart?.state ?? editorState}
          />
        </div>
      </div>
    </div>
  );
};

export default ChartEditPage;